import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, ListChecks, Save, Lock } from "lucide-react";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const LOCKED_KEY = "bank_application_no";
const LOCKED_DEFAULT = { key: LOCKED_KEY, label: "Bank Application No", required: true, locked: true };
const withLocked = (fields) => {
  const list = Array.isArray(fields) ? [...fields] : [];
  const has = list.some((f) => (f.key || "") === LOCKED_KEY);
  return has ? list : [LOCKED_DEFAULT, ...list];
};

export default function LoginFields() {
  const [types, setTypes] = useState([]);
  const [loanTypeCatalog, setLoanTypeCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/login-fields").then((r) => setTypes(r.data.loan_types || [])).catch(() => {}),
      api.get("/meta").then((r) => setLoanTypeCatalog(r.data.loan_types || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const addType = () => setTypes((t) => [...t, { name: "", fields: [LOCKED_DEFAULT] }]);
  const delType = (i) => setTypes((t) => t.filter((_, ix) => ix !== i));
  const updType = (i, patch) => setTypes((t) => t.map((x, ix) => (ix === i ? { ...x, ...patch } : x)));
  const addField = (i) => updType(i, { fields: [...(types[i].fields || []), { key: "", label: "", required: false, locked: false }] });
  const delField = (i, j) => {
    const target = (types[i]?.fields || [])[j];
    if (target?.locked) { toast.error("This field is locked and cannot be removed"); return; }
    updType(i, { fields: types[i].fields.filter((_, jx) => jx !== j) });
  };
  const updField = (i, j, patch) => updType(i, { fields: types[i].fields.map((f, jx) => (jx === j ? { ...f, ...patch } : f)) });

  const save = async () => {
    setSaving(true);
    try {
      const clean = types
        .filter((t) => (t.name || "").trim())
        .map((t) => ({
          name: t.name.trim(),
          fields: withLocked(
            (t.fields || [])
              .filter((f) => (f.label || "").trim() || f.locked)
              .map((f) => ({
                key: (f.key || slugify(f.label)).trim(),
                label: (f.label || "").trim() || f.key,
                required: !!f.required || !!f.locked,
                locked: !!f.locked,
              }))
          ),
        }));
      const { data } = await api.put("/settings/login-fields", { loan_types: clean });
      setTypes(data.loan_types || []);
      toast.success("Login Done inputs saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-24 text-center text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="login-fields-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2">
          <ListChecks size={24} className="text-emerald-600" /> Login Done Inputs
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Define the inputs required before a lead moves to <b>Login Done</b>, <b>per loan type</b>. Every field marked
          <b> Required</b> must be filled — otherwise the stage transition is blocked. The <b>Bank Application No</b> field
          is locked because it drives the Undertaking Letter, PDF proposals and reports.
        </p>
        {loanTypeCatalog.length > 0 && (
          <p className="text-xs text-slate-400 mt-1">Available loan types: {loanTypeCatalog.join(", ")}</p>
        )}
      </div>

      <button
        data-testid="login-fields-add-loan-type"
        onClick={addType}
        className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm inline-flex items-center gap-2"
      >
        <Plus size={16} /> Add Loan Type
      </button>

      {types.length === 0 && (
        <p className="text-sm text-slate-400">No loan types configured. Click "Add Loan Type" to begin.</p>
      )}

      {types.map((t, i) => (
        <div
          key={i}
          data-testid={`login-fields-loan-type-${i}`}
          className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3"
        >
          <div className="flex items-center gap-2">
            <input
              list={`login-fields-lt-list-${i}`}
              data-testid={`login-fields-loan-type-name-${i}`}
              value={t.name}
              onChange={(e) => updType(i, { name: e.target.value })}
              className={`${inputCls} flex-1`}
              placeholder="Loan Type (e.g. Home Loan)"
            />
            <datalist id={`login-fields-lt-list-${i}`}>
              {loanTypeCatalog.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <button
              data-testid={`login-fields-del-loan-type-${i}`}
              onClick={() => delType(i)}
              className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
            >
              <Trash2 size={16} />
            </button>
          </div>

          <div className="grid grid-cols-12 gap-2 px-1 pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            <div className="col-span-6">Field Label</div>
            <div className="col-span-3">Key</div>
            <div className="col-span-2">Required</div>
            <div className="col-span-1" />
          </div>

          {withLocked(t.fields || []).map((f, j) => (
            <div
              key={j}
              className="grid grid-cols-12 gap-2 items-center"
              data-testid={`login-fields-row-${i}-${j}`}
            >
              <input
                value={f.label}
                onChange={(e) =>
                  updField(i, j, {
                    label: e.target.value,
                    key: f.locked ? f.key : slugify(e.target.value),
                  })
                }
                disabled={!!f.locked}
                className={`${inputCls} col-span-6 ${f.locked ? "bg-slate-50 text-slate-500" : ""}`}
                placeholder="e.g. Sanction ID"
                data-testid={`login-fields-label-${i}-${j}`}
              />
              <input
                value={f.key}
                onChange={(e) => updField(i, j, { key: slugify(e.target.value) })}
                disabled={!!f.locked}
                className={`${inputCls} col-span-3 font-mono text-xs ${f.locked ? "bg-slate-50 text-slate-500" : ""}`}
                placeholder="auto"
                data-testid={`login-fields-key-${i}-${j}`}
              />
              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!f.required || !!f.locked}
                  disabled={!!f.locked}
                  onChange={(e) => updField(i, j, { required: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid={`login-fields-required-${i}-${j}`}
                />
                Required
              </label>
              <div className="col-span-1 justify-self-end">
                {f.locked ? (
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-300"
                    title="Locked — cannot delete"
                  >
                    <Lock size={15} />
                  </span>
                ) : (
                  <button
                    onClick={() => delField(i, j)}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                    data-testid={`login-fields-del-${i}-${j}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            data-testid={`login-fields-add-field-${i}`}
            onClick={() => addField(i)}
            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium inline-flex items-center gap-1"
          >
            <Plus size={13} /> Add Field
          </button>
        </div>
      ))}

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className={btnPrimary}
          data-testid="login-fields-save"
        >
          <Save size={16} /> Save Settings
        </button>
      </div>
    </div>
  );
}
